import React, { useState, useEffect, useCallback } from 'react';
import { Spinner, Alert } from 'react-bootstrap';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getAppliedRoleListForDrCr, fetchUserJogetProfileApi, saveApplicationReview, incrementKnowMoreApi, fetchCurrentAuditionDetailsApi } from '../../services/api'; 
import MetaTags from '../../components/MetaTags'; 
import './ManageApplicationsPage.css'; 
import Swal from "sweetalert2"; 

import HeaderSection from '../../components/applicationDetails/HeaderSection';
import VideoAuditionSection from '../../components/applicationDetails/VideoAuditionSection';
import NonVideoAuditionSection from '../../components/applicationDetails/NonVideoAuditionSection';
import PendingAiAnalysisCard from '../../components/applicationDetails/PendingAiAnalysisCard';
import { useAuth } from '../../context/AuthContext';

const calculateAge = (dobString) => {
    if (!dobString) return '---';
    const [month, day, year] = dobString.split('/').map(Number);
    const birthDate = new Date(year, month - 1, day); 
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
    return age;
};

const isMeetingExpired = (meetingEndString, meetingStartString) => {
    const targetDate = meetingEndString || meetingStartString;
    if (!targetDate) return false;
    return new Date().getTime() > new Date(targetDate).getTime();
};

const ManageApplicationDetails = () => {
    const { id: roleApplicationId } = useParams(); 
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    
    const [selectedApp, setSelectedApp] = useState(null);
    const [talentProfile, setTalentProfile] = useState(null);
    const [loadingApp, setLoadingApp] = useState(true);
    const [error, setError] = useState('');
    const [reviewText, setReviewText] = useState('');
    const [ratingStar, setRatingStar] = useState(0); 
    const [hoverRating, setHoverRating] = useState(0); 
    const [decision, setDecision] = useState(''); 
    const [saveReviewLoading, setSaveReviewLoading] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({}); 

    const isAdmin = !!localStorage.getItem("adminToken");

    const hasPermission = (moduleKey, action = 'View') => {
        if (String(user?.roleCode || user?.role_code) === '900') {
            return (user?.permissions || []).includes(`${moduleKey}:${action}`);
        }
        return true;
    };

    const isReviewSubmitted = () => {
        if (!selectedApp) return false;
        const roleCode = String(user?.roleCode || user?.role_code || '');
        const roleName = String(user?.role || '').toLowerCase();
        if (roleCode === '900' || roleName === 'staff') return !!(selectedApp.staffComment?.trim());
        if (isAdmin) return !!(selectedApp.modified_by_admin?.trim());
        return !!((selectedApp.review || selectedApp.c_review)?.trim());
    };

    const handleBack = () => {
        if (location.pathname.includes('/admin')) navigate('/admin/audition-list'); 
        else navigate('/dashboard/manage-applications');
    };

    const handleShare = async () => {
        const shareTitle = `Audition Analysis - ${selectedApp?.talent_name || 'Candidate'}`;
        const shareText = `Check out acting audition analysis report for ${selectedApp?.talent_name || 'Candidate'} - Role: ${selectedApp?.c_role_name || 'N/A'}`;
        const shareUrl = window.location.href;
        if (navigator.share) {
            try { await navigator.share({ title: shareTitle, text: shareText, url: shareUrl }); } catch (err) {}
        } else {
            try {
                await navigator.clipboard.writeText(shareUrl);
                Swal.fire({ icon: 'success', title: 'Link Copied!', text: 'Audition report link copied.', width: '380px', background: '#131824', color: '#fff', confirmButtonColor: '#dc3545' });
            } catch (err) {}
        }
    };

    const loadApplicationAndProfile = useCallback(async (isSilent = false) => {
        if (!isSilent) setLoadingApp(true);
        setError('');
        try {
            const detailRes = await fetchCurrentAuditionDetailsApi(roleApplicationId).catch(() => ({ data: { data: {} } }));
            const detailData = detailRes.data?.data || {};
            const response = await getAppliedRoleListForDrCr();
            const foundApp = (response.data.data || []).find(item => item.roleApplicationId === roleApplicationId);

            if (!foundApp) {
                setError('Application not found.');
                if (!isSilent) setLoadingApp(false);
                return;
            }

            const candidateId = detailData.userId || detailData.c_userId || foundApp.candidateJogetId || foundApp.c_userId || foundApp.userId || foundApp.appliedByUser;
            let aiData = detailData.aiAnalysis || foundApp.aiAnalysis || foundApp.ai_analysis_data;
            if (typeof aiData === 'string') { try { aiData = JSON.parse(aiData); } catch(e) { aiData = {}; } }
            if (!aiData || typeof aiData !== 'object') { aiData = {}; }

            const rawResult = String(detailData.result || detailData.c_result || foundApp.c_result || foundApp.result || '').toUpperCase();
            const initialDecision = ["SELECTED", "REJECTED", "RESUBMIT"].includes(rawResult) ? rawResult : '';

            const roleCode = String(user?.roleCode || user?.role_code || '');
            const roleName = String(user?.role || '').toLowerCase();
            let savedReview = (roleCode === '900' || roleName === 'staff') ? (detailData.staffComment || foundApp.staffComment || '') : (isAdmin ? (detailData.modified_by_admin || foundApp.modified_by_admin || '') : (detailData.review || detailData.c_review || foundApp.c_review || foundApp.review || ''));
            let savedRating = detailData.ratingStar || detailData.c_ratingStar || foundApp.c_ratingStar || foundApp.ratingStar || '0';

            setSelectedApp({
                ...foundApp, ...detailData,
                talent_name: foundApp.candidateName || detailData.candidateName || 'Candidate',
                aiAnalysis: aiData,
                c_role_name: detailData.roleName || foundApp.c_roleName || 'N/A',
                c_project_name: detailData.projectName || foundApp.projectName || 'N/A',
                c_roleScript: detailData.roleScript || foundApp.c_roleScript || 'No script provided.',
                c_videoUrl: detailData.videoUrl || foundApp.c_videoUrl || null,
                bookingType: detailData.bookingType || foundApp.booking_type || 'VIDEO_UPLOAD',
                talent_id: candidateId || foundApp.appliedByUser || detailData.userId,
                c_result: initialDecision || 'Pending'
            });

            setReviewText(savedReview);
            setRatingStar(parseInt(savedRating, 10) || 0);
            setDecision(initialDecision);

            if (candidateId) {
                const res = await fetchUserJogetProfileApi(candidateId);
                setTalentProfile(res.data?.jogetData || {});
            }
        } catch (err) { setError('Failed to load application details.'); }
        finally { if (!isSilent) setLoadingApp(false); }
    }, [roleApplicationId, isAdmin, user]);

    useEffect(() => { if (roleApplicationId) loadApplicationAndProfile(); }, [roleApplicationId, loadApplicationAndProfile]);

    const handleSaveReview = async () => {
        const errors = {};
        if (!ratingStar) errors.ratingStar = "Please select a rating.";
        if (!reviewText?.trim()) errors.reviewText = "Please enter review text.";
        if (!decision) errors.decision = "Please select a status.";
        if (Object.keys(errors).length > 0) return setFieldErrors(errors);

        setFieldErrors({});
        setSaveReviewLoading(true);
        try {
            const payload = { roleId: selectedApp.c_roleId || selectedApp.roleId, userId: selectedApp.talent_id || selectedApp.appliedByUser, addedBy: selectedApp.drcrUserId, applicationId: roleApplicationId, review: reviewText, ratingStar: ratingStar.toString(), result: decision, isAdmin, userRoleCode: user?.roleCode || user?.role_code || '' };
            const res = await saveApplicationReview(payload);
            if (res.data?.success || res.success) {
                await loadApplicationAndProfile(true);
                Swal.fire({ icon: "success", title: "Success!", text: "Review saved!", width: '380px', background: '#131824', color: '#fff', confirmButtonColor: "#dc3545" });
            } else { Swal.fire({ icon: "error", title: "Failed", text: res.data?.message || "Could not save review.", width: '380px', background: '#131824', color: '#fff' }); }
        } catch (err) { Swal.fire({ icon: "error", title: "Error", text: err.response?.data?.message || "Failed to save review.", width: '380px', background: '#131824', color: '#fff' }); }
        finally { setSaveReviewLoading(false); }
    };

    const handleViewCandidateProfile = async () => {
        const targetId = selectedApp?.talent_id || selectedApp?.userId || selectedApp?.appliedByUser || selectedApp?.candidateJogetId || talentProfile?.userId;
        if (!targetId) return Swal.fire({ icon: 'warning', title: 'Notice', text: 'Profile ID not available.', width: '380px', background: '#131824', color: '#fff' });
        try { await incrementKnowMoreApi(targetId).catch(() => {}); } catch (e) {}
        navigate(location.pathname.includes('/admin') ? `/admin/candidate-profile/${targetId}?knowMore=true&viewOnly=true` : `/dashboard/candidate-profile/${targetId}?knowMore=true&viewOnly=true&isCD=true`);
    };

    if (loadingApp) return <div className="text-center p-5"><Spinner className="text-white" animation="border" variant="danger" /></div>;
    if (error && !selectedApp) return <Alert variant="danger" className="m-3">{error}</Alert>;
    if (!selectedApp) return <Alert variant="info" className="m-3">Application details could not be loaded.</Alert>;

    const isVideoUpload = String(selectedApp.bookingType || "").toUpperCase() === "VIDEO_UPLOAD";

    return (
        <div className="bma-audition-analysis-page container-fluid py-4 px-xl-5 text-white">
            <MetaTags title="Audition Analysis - BookMyAudition" />
            <HeaderSection handleBack={handleBack} selectedApp={selectedApp} handleShare={handleShare} />

            {isVideoUpload ? (
                selectedApp.aiAnalysis ? (
                    <VideoAuditionSection 
                        selectedApp={selectedApp} talentAge={calculateAge(talentProfile?.dateOfBirth)} tp={talentProfile || {}}
                        handleViewCandidateProfile={handleViewCandidateProfile} ai={selectedApp.aiAnalysis}
                        hasPermission={hasPermission} isReviewSubmitted={isReviewSubmitted}
                        hoverRating={hoverRating} setHoverRating={setHoverRating} ratingStar={ratingStar} setRatingStar={setRatingStar}
                        reviewText={reviewText} setReviewText={setReviewText} decision={decision} setDecision={setDecision}
                        handleSaveReview={handleSaveReview} saveReviewLoading={saveReviewLoading} fieldErrors={fieldErrors} setFieldErrors={setFieldErrors}
                    />
                ) : (
                    <PendingAiAnalysisCard selectedApp={selectedApp} roleApplicationId={roleApplicationId} />
                )
            ) : (
                <NonVideoAuditionSection 
                    selectedApp={selectedApp} expired={isMeetingExpired(selectedApp.meetingEnd, selectedApp.meetingStart)}
                    talentAge={calculateAge(talentProfile?.dateOfBirth)} tp={talentProfile || {}}
                    handleViewCandidateProfile={handleViewCandidateProfile} hasPermission={hasPermission} isReviewSubmitted={isReviewSubmitted}
                    hoverRating={hoverRating} setHoverRating={setHoverRating} ratingStar={ratingStar} setRatingStar={setRatingStar}
                    reviewText={reviewText} setReviewText={setReviewText} decision={decision} setDecision={setDecision}
                    handleSaveReview={handleSaveReview} saveReviewLoading={saveReviewLoading} fieldErrors={fieldErrors} setFieldErrors={setFieldErrors}
                />
            )}
        </div>
    );
};

export default ManageApplicationDetails;